import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }
interface Body { x: number; v: number; m: number }

// Va chạm & bảo toàn động lượng (Lớp 10 — Bài 16/17): hai xe va chạm trên đường thẳng.
// p = Σmv luôn bảo toàn. Đàn hồi (e=1): bảo toàn động năng; mềm/dính (e=0). Đồ thị v–t kiểu Pasco.
const CollisionSimulation: React.FC<Props> = ({
  title = 'Va chạm & bảo toàn động lượng',
  description = 'Hai xe va chạm trên ray. Quan sát vec tơ động lượng đặt tại tâm xe và đồ thị vận tốc theo thời gian (như cảm biến Pasco).',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const gcv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const lastRef = useRef<number | null>(null)
  const b1 = useRef<Body>({ x: 120, v: 70, m: 3 })
  const b2 = useRef<Body>({ x: 460, v: 0, m: 3 })
  const x1Init = useRef(120), x2Init = useRef(460)  // vị trí ban đầu (kéo tay để đổi)
  const drag = useRef<null | 1 | 2>(null)
  const collided = useRef(false)
  const inCol = useRef(false), colT = useRef(0)            // pha va chạm hữu hạn (như đệm xe Pasco)
  const vFrom = useRef<[number, number]>([0, 0]), vTo = useRef<[number, number]>([0, 0])
  const tRef = useRef(0)
  const hist = useRef<{ t: number; v1: number; v2: number }[]>([])
  const [m1, setM1] = useState(3), [m2, setM2] = useState(3)
  const [v1, setV1] = useState(70), [v2, setV2] = useState(0)
  const [e, setE] = useState(1)
  const [running, setRunning] = useState(false)
  const [, force] = useState(0)
  const firstRef = useRef(false)
  const W = 680, H = 190, GW = 680, GH = 230, FLOOR = 150
  const COL_TAU = 0.16  // thời gian va chạm hữu hạn → đồ thị chuyển tiếp mượt như cảm biến Pasco

  const sizeOf = (m: number) => 26 + m * 8

  const reset = useCallback(() => {
    setRunning(false); collided.current = false; inCol.current = false; tRef.current = 0
    b1.current = { x: x1Init.current, v: v1, m: m1 }; b2.current = { x: x2Init.current, v: v2, m: m2 }
    hist.current = [{ t: 0, v1, v2 }]
    force((n) => n + 1)
  }, [m1, m2, v1, v2])
  useEffect(() => { reset() }, [m1, m2, v1, v2]) // eslint-disable-line react-hooks/exhaustive-deps

  const arrowH = (ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, color: string) => {
    if (Math.abs(dx) < 3) return
    const ex = x + dx, dir = Math.sign(dx)
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 3; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, y); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(ex, y); ctx.lineTo(ex - dir * 9, y - 5); ctx.lineTo(ex - dir * 9, y + 5); ctx.closePath(); ctx.fill()
    ctx.lineCap = 'butt'
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(0, FLOOR, W, 6) // ray
    const A = b1.current, B = b2.current
    const drawBox = (b: Body, color: string, edge: string, label: string, pLabel: string) => {
      const s = sizeOf(b.m)
      const g = ctx.createLinearGradient(0, FLOOR - s, 0, FLOOR); g.addColorStop(0, color); g.addColorStop(1, edge)
      ctx.fillStyle = g; ctx.fillRect(b.x - s / 2, FLOOR - s, s, s)
      ctx.strokeStyle = edge; ctx.lineWidth = 2; ctx.strokeRect(b.x - s / 2, FLOOR - s, s, s)
      ctx.fillStyle = '#fff'; ctx.font = 'bold 12px sans-serif'; ctx.textAlign = 'center'
      ctx.fillText(`${b.m}kg`, b.x, FLOOR - 7); ctx.textAlign = 'left'
      // VEC TƠ ĐỘNG LƯỢNG p = m·v — ĐIỂM ĐẶT tại tâm xe
      const py = FLOOR - s / 2
      const plen = Math.sign(b.v) * Math.min(135, Math.abs(b.m * b.v) * 0.24)
      arrowH(ctx, b.x, py, plen, '#7c3aed')
      ctx.fillStyle = '#7c3aed'; ctx.beginPath(); ctx.arc(b.x, py, 3.5, 0, Math.PI * 2); ctx.fill() // điểm đặt
      if (Math.abs(plen) > 3) { ctx.font = 'bold 12px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(pLabel, b.x + plen / 2, py - 9); ctx.textAlign = 'left' }
    }
    drawBox(A, '#93c5fd', '#1d4ed8', '', 'p₁')
    drawBox(B, '#fca5a5', '#b91c1c', '', 'p₂')
    const p = (A.m * A.v + B.m * B.v).toFixed(0)
    const ke = (0.5 * A.m * A.v * A.v + 0.5 * B.m * B.v * B.v).toFixed(0)
    ctx.fillStyle = '#334155'; ctx.font = 'bold 13px sans-serif'
    ctx.fillText(`Động lượng p = ${p}   |   Động năng Wđ = ${ke}`, 16, 22)
  }, [])

  const drawGraph = useCallback(() => {
    const ctx = prepCanvas(gcv.current, GW, GH); if (!ctx) return
    const x0 = 54, mid = GH / 2, plotW = GW - x0 - 16, plotH = mid - 18
    const h = hist.current
    let mx = 10; for (const pt of h) mx = Math.max(mx, Math.abs(pt.v1), Math.abs(pt.v2))
    const step = mx <= 100 ? 25 : mx <= 200 ? 50 : 100   // tỉ lệ xích phù hợp
    const vmax = Math.max(step, Math.ceil(mx / step) * step)
    const tmax = Math.max(2, h.length ? h[h.length - 1].t : 2)
    const X = (t: number) => x0 + (t / tmax) * plotW
    const Y = (v: number) => mid - (v / vmax) * plotH
    // lưới ngang + nhãn tỉ lệ trục v
    ctx.font = '10px sans-serif'; ctx.textAlign = 'right'
    for (let val = step; val <= vmax; val += step) {
      for (const sv of [val, -val]) {
        ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(x0, Y(sv)); ctx.lineTo(x0 + plotW, Y(sv)); ctx.stroke()
        ctx.fillStyle = '#94a3b8'; ctx.fillText(String(sv), x0 - 6, Y(sv) + 3)
      }
    }
    ctx.textAlign = 'left'
    // trục đậm + mũi tên (v đứng, t ngang tại v=0)
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x0, 12); ctx.lineTo(x0, GH - 8); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, mid); ctx.lineTo(GW - 10, mid); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, 6); ctx.lineTo(x0 - 4, 14); ctx.lineTo(x0 + 4, 14); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(GW - 5, mid); ctx.lineTo(GW - 13, mid - 4); ctx.lineTo(GW - 13, mid + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('v (px/s)', x0 + 4, 11); ctx.fillText('t (s)', GW - 38, mid - 6); ctx.fillStyle = '#64748b'; ctx.fillText('0', x0 - 14, mid + 4)
    // hai đường v(t)
    const line = (key: 'v1' | 'v2', color: string) => {
      ctx.strokeStyle = color; ctx.lineWidth = 2.5; ctx.beginPath()
      h.forEach((pt, i) => { const px = X(pt.t), py = Y(pt[key]); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) })
      ctx.stroke()
      const last = h[h.length - 1]; if (last) { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(X(last.t), Y(last[key]), 4, 0, Math.PI * 2); ctx.fill() }
    }
    line('v1', '#2563eb'); line('v2', '#dc2626')
    // chú thích
    ctx.font = 'bold 11px sans-serif'
    ctx.fillStyle = '#2563eb'; ctx.fillText('■ Xe 1', GW - 130, 16)
    ctx.fillStyle = '#dc2626'; ctx.fillText('■ Xe 2', GW - 70, 16)
  }, [])

  const render = useCallback(() => { draw(); drawGraph() }, [draw, drawGraph])

  // Kéo tay để sắp xếp vị trí 2 xe (chỉ khi chưa chạy)
  const posX = (ev: React.PointerEvent) => {
    const rect = cv.current!.getBoundingClientRect()
    return (ev.clientX - rect.left) * (W / rect.width)
  }
  const down = (ev: React.PointerEvent) => {
    if (running) return
    const x = posX(ev)
    if (Math.abs(x - b1.current.x) < sizeOf(b1.current.m) / 2 + 10) drag.current = 1
    else if (Math.abs(x - b2.current.x) < sizeOf(b2.current.m) / 2 + 10) drag.current = 2
  }
  const move = (ev: React.PointerEvent) => {
    if (!drag.current || running) return
    let x = Math.max(40, Math.min(W - 40, posX(ev)))
    const gap = (sizeOf(b1.current.m) + sizeOf(b2.current.m)) / 2 + 6
    if (drag.current === 1) { x = Math.min(x, b2.current.x - gap); b1.current.x = x; x1Init.current = x }
    else { x = Math.max(x, b1.current.x + gap); b2.current.x = x; x2Init.current = x }
    force((n) => n + 1); render()
  }
  const up = () => { drag.current = null }

  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.05, (ts - lastRef.current) / 1000); lastRef.current = ts
    const A = b1.current, B = b2.current
    const s1 = sizeOf(A.m), s2 = sizeOf(B.m), minGap = (s1 + s2) / 2
    // bắt đầu va chạm: tính vận tốc đích, mở pha va chạm hữu hạn
    if (!collided.current && B.x - A.x <= minGap && A.v - B.v > 0) {
      collided.current = true; inCol.current = true; colT.current = 0
      const M = A.m + B.m, a0 = A.v, b0 = B.v
      vFrom.current = [a0, b0]
      vTo.current = [
        (A.m * a0 + B.m * b0 + B.m * e * (b0 - a0)) / M,
        (A.m * a0 + B.m * b0 + A.m * e * (a0 - b0)) / M,
      ]
    }
    // trong pha va chạm: nội suy vận tốc mượt (smoothstep) → đường cong S
    if (inCol.current) {
      colT.current += dt
      const u = Math.min(1, colT.current / COL_TAU), sm = u * u * (3 - 2 * u)
      A.v = vFrom.current[0] + (vTo.current[0] - vFrom.current[0]) * sm
      B.v = vFrom.current[1] + (vTo.current[1] - vFrom.current[1]) * sm
      if (u >= 1) inCol.current = false
    }
    A.x += A.v * dt; B.x += B.v * dt
    tRef.current += dt
    // giữ 2 xe tiếp xúc (không lồng nhau) trong lúc va chạm
    if (inCol.current && B.x - A.x < minGap) { const mid = (A.x + B.x) / 2; A.x = mid - minGap / 2; B.x = mid + minGap / 2 }
    // chặn biên: gặp biên thì DỪNG
    if (A.x <= 20) { A.x = 20; A.v = 0 }
    if (A.x >= W - 20) { A.x = W - 20; A.v = 0 }
    if (B.x <= 20) { B.x = 20; B.v = 0 }
    if (B.x >= W - 20) { B.x = W - 20; B.v = 0 }
    hist.current.push({ t: tRef.current, v1: A.v, v2: B.v })
    if (hist.current.length > 4000) hist.current.shift()
    force((n) => n + 1); render()
    if (Math.abs(A.v) < 0.5 && Math.abs(B.v) < 0.5 && collided.current && !inCol.current) { setRunning(false); return }
    raf.current = requestAnimationFrame(tick)
  }, [e, render])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])
  useEffect(() => { render() }, [render])

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className={`w-full rounded-lg border border-gray-100 bg-slate-50 touch-none ${running ? '' : 'cursor-grab'}`} />
      <canvas ref={gcv} width={GW} height={GH} className="w-full mt-2 rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-16 text-gray-600">m₁</span><input type="range" min={1} max={6} value={m1} onChange={(e) => setM1(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right">{m1}kg</span></label>
        <label className="flex items-center gap-2"><span className="w-16 text-gray-600">m₂</span><input type="range" min={1} max={6} value={m2} onChange={(e) => setM2(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right">{m2}kg</span></label>
        <label className="flex items-center gap-2"><span className="w-16 text-gray-600">Loại</span><select value={e} onChange={(ev) => setE(Number(ev.target.value))} className="flex-1 px-2 py-1 rounded-lg border border-gray-200 bg-white"><option value={1}>Va chạm đàn hồi</option><option value={0}>Va chạm mềm (dính)</option></select></label>
        <label className="flex items-center gap-2"><span className="w-16 text-gray-600">v₁</span><input type="range" min={-100} max={150} step={10} value={v1} onChange={(e) => setV1(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right">{v1}</span></label>
        <label className="flex items-center gap-2"><span className="w-16 text-gray-600">v₂</span><input type="range" min={-150} max={100} step={10} value={v2} onChange={(e) => setV2(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right">{v2}</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">Mẹo: kéo 2 xe bằng tay để sắp xếp vị trí ban đầu; nút <b>Đặt lại</b> đưa xe về đúng vị trí đã sắp.</p>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { reset(); setRunning(true) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700"><Play className="w-4 h-4" /> Chạy</button>
        <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
    </div>
  )
}
export default CollisionSimulation
